import React, { useState, useEffect, useRef } from "react";
import { api } from "../../services/api";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import { Box, Button, Paper, Typography, CircularProgress, } from "@mui/material";
import CreateZonaModal from "../../components/modales/ubicaciones/CreateZonaModal";
import EditZonaModal from "../../components/modales/ubicaciones/EditZonaModal";
import EditorLayout from "../../components/modales/ubicaciones/EditorLayout";
import withAuthenticationRequired from "../../components/ProtectedRoutes/withAuthenticationRequired";
import { Stage, Layer, Rect, Text, Transformer } from 'react-konva';
import { Add, Save, Edit, Delete, Visibility } from "@mui/icons-material";
import DisponibilidadModal from "../../components/modales/ubicaciones/DisponibilidadModal";

export const Ubicaciones = () => {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState(null);
  const { id: eventoId } = useParams();
  const [state, setState] = useState({
    showResult: false,
    apiMessage: "",
    error: null,
  });
  const [loading, setLoading] = useState(true);
  const [modoCreacion, setModoCreacion] = useState(false);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const stageRef = useRef();

  const [showEditModal, setShowEditModal] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);

  const paperRef = useRef();
  const [canvasSize, setCanvasSize] = useState({ width: 700, height: 500 });

  useEffect(() => {
    if (!loading && paperRef.current) {
      const { offsetWidth, offsetHeight } = paperRef.current;
      setCanvasSize({
        width: offsetWidth,
        height: offsetHeight
      });
    }
  }, [loading]);

  useEffect(() => {
    const updateSize = () => {
      if (paperRef.current) {
        const { offsetWidth, offsetHeight } = paperRef.current;
        setCanvasSize({
          width: offsetWidth,
          height: offsetHeight
        });
      }
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (paperRef.current) {
        const { offsetWidth, offsetHeight } = paperRef.current;
        setCanvasSize({
          width: offsetWidth,
          height: offsetHeight
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [loading, ubicaciones, showLayoutEditor, showCreateModal, showEditModal]);

  const handleZonaTransform = (e, zona) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // reseteamos la escala y aplicamos
    node.scaleX(1);
    node.scaleY(1);

    const newUbicaciones = ubicaciones.map(ub => {
      if (ub.id === zona.id) {
        return {
          ...ub,
          ancho: Math.max(50, Math.round(zona.ancho * scaleX)), //min 50px
          alto: Math.max(30, Math.round(zona.alto * scaleY))    //min 30px
        };
      }
      return ub;
    });
    setUbicaciones(newUbicaciones);
  };

  const canvasContainerRef = useRef();

  useEffect(() => {
    const callApi = async () => {
      try {
        const response = await api.ubicacionesNew.getAllCompletas(eventoId);
        /* console.log("Response del API:", response); */
        if (response.status === "success") {
          const ubicacionesData = Array.isArray(response.data) ? response.data :
            (response.data?.ubicaciones && Array.isArray(response.data.ubicaciones)) ? response.data.ubicaciones : [];

          const ubicacionesCorregidas = ubicacionesData.map((ubicacion, index) => {
            if (ubicacion.coordX === 0 && ubicacion.coordY === 0 && ubicacion.ancho === 0 && ubicacion.alto === 0) {
              return {
                ...ubicacion,
                coordX: 50 + (index * 160), // posicionar en fila
                coordY: 50,
                ancho: 150,
                alto: 100
              };
            }
            return ubicacion;
          });

          setUbicaciones(ubicacionesCorregidas);
        } else {
          setUbicaciones([]);
        }
      } catch (error) {
        toast.error("Error en la Red.", {
          position: "top-right",
          autoClose: 4000,
        });
        setState(prevState => ({
          ...prevState,
          error: "",
        }));
      } finally {
        setLoading(false);
      }
    };

    callApi();
    document.title = `Ubicaciones del Evento | PassGo`;
  }, [eventoId]);

  const actualizarZona = async (datosActualizados) => {
    if (!ubicacionSeleccionada) return;

    try {
      const response = await api.ubicacionesNew.updateUbicacion(ubicacionSeleccionada.id, {
        coordX: ubicacionSeleccionada.coordX,
        coordY: ubicacionSeleccionada.coordY,
        ancho: ubicacionSeleccionada.ancho,
        alto: ubicacionSeleccionada.alto,
        ubicacion: datosActualizados.nombre,
        limite_persona: datosActualizados.limitePorPersona,
        limite_maximo: datosActualizados.limiteMaximo,
        tipo_ubicacion: datosActualizados.tipoUbicacion,
        disenoJSON: ubicacionSeleccionada.disenoJSON || ""
      });

      if (response.status === "success") {
        // recargar las ubicaciones
        const refreshResponse = await api.ubicacionesNew.getAllCompletas(eventoId);
        if (refreshResponse.status === "success") {
          const ubicacionesData = Array.isArray(refreshResponse.data) ? refreshResponse.data :
            (refreshResponse.data?.ubicaciones && Array.isArray(refreshResponse.data.ubicaciones)) ? refreshResponse.data.ubicaciones : [];
          setUbicaciones(ubicacionesData);

          const zonaActualizada = ubicacionesData.find(z => z.id === ubicacionSeleccionada.id);
          if (zonaActualizada) {
            setUbicacionSeleccionada(zonaActualizada);
          }
        }

        setShowEditModal(false);
        toast.success("Zona actualizada exitosamente.", {
          position: "top-right",
          autoClose: 3000,
        });
        setState({
          ...state,
          apiMessage: "",
        });
      }
    } catch (error) {
      toast.error("Error al actualizar la zona.", {
        position: "top-right",
        autoClose: 4000,
      });
      setState({
        ...state,
        error: "",
      });
    }
  };

  // creacion de nueva zona
  const handleCanvasClick = (e) => {
    if (!modoCreacion) return;

    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();

    // abrimos modal y guardamos pos
    setClickPosition({ x: pointer.x, y: pointer.y });
    setShowCreateModal(true);
    setModoCreacion(false);
  };

  const crearZonaDesdeModal = async (datosZona) => {
    try {
      const dataToSend = {
        coordX: Math.round(clickPosition.x),
        coordY: Math.round(clickPosition.y),
        ancho: 150,
        alto: 100,
        ubicacion: datosZona.nombre,
        limite_persona: datosZona.limitePorPersona,
        limite_maximo: datosZona.limiteMaximo,
        tipo_ubicacion: datosZona.tipoUbicacion,
        disenoJSON: "",
        idevento: parseInt(eventoId)
      };

      const response = await api.ubicacionesNew.createUbicacion(dataToSend);
      if (response.status === "success") {
        const refreshResponse = await api.ubicacionesNew.getAllCompletas(eventoId);
        if (refreshResponse.status === "success") {
          const ubicacionesData = Array.isArray(refreshResponse.data) ? refreshResponse.data :
            (refreshResponse.data?.ubicaciones && Array.isArray(refreshResponse.data.ubicaciones)) ? refreshResponse.data.ubicaciones : [];
          setUbicaciones(ubicacionesData);
        }

        setShowCreateModal(false);
        setClickPosition(null);
        toast.success("Zona creada exitosamente.", {
          position: "top-right",
          autoClose: 3000,
        });
        setState({
          ...state,
          apiMessage: "",
        });
      }
    } catch (error) {
      toast.error("Error al crear la zona.", {
        position: "top-right",
        autoClose: 4000,
      });
      setState({
        ...state,
        error: "",
      });
    }
  };

  const guardarDiseno = async () => {
    try {
      // iteramos sobre todas las zonas
      for (const ubicacion of ubicaciones) {
        await api.ubicacionesNew.updateUbicacion(ubicacion.id, {
          coordX: Math.round(ubicacion.coordX),
          coordY: Math.round(ubicacion.coordY),
          ancho: ubicacion.ancho,
          alto: ubicacion.alto,
          ubicacion: ubicacion.ubicacion,
          limite_persona: ubicacion.limite_persona,
          limite_maximo: ubicacion.limite_maximo,
          tipo_ubicacion: ubicacion.tipo_ubicacion,
          disenoJSON: ubicacion.disenoJSON
        });
      }

      toast.success("Diseño guardado exitosamente.", {
        position: "top-right",
        autoClose: 3000,
      });
      setState({
        ...state,
        apiMessage: "",
      });
    } catch (error) {
      toast.error("Error al guardar el diseño.", {
        position: "top-right",
        autoClose: 4000,
      });
      setState({
        ...state,
        error: "",
      });
    }
  };

  const handleZonaDrag = (e, zona) => {
    const newUbicaciones = ubicaciones?.map(ub => {
      if (ub.id === zona.id) {
        return {
          ...ub,
          coordX: e.target.x(),
          coordY: e.target.y()
        };
      }
      return ub;
    });
    setUbicaciones(newUbicaciones);
  };

  const handleZonaClick = (zona) => {
    setUbicacionSeleccionada(zona);
  };

  const abrirEditorLayout = (zona) => {
    setUbicacionSeleccionada(zona);
    setShowLayoutEditor(true);
  };

  const eliminarZonaSeleccionada = async () => {
    if (!ubicacionSeleccionada) return;

    const confirmar = window.confirm(`¿Eliminar la zona "${ubicacionSeleccionada.ubicacion}"?\nEsta acción no se puede deshacer.`);
    if (!confirmar) return;

    try {
      const response = await api.ubicacionesNew.deleteUbicacion(ubicacionSeleccionada.id);
      if (response.status === "success") {
        const refreshResponse = await api.ubicacionesNew.getAllCompletas(eventoId);
        if (refreshResponse.status === "success") {
          const ubicacionesData = Array.isArray(refreshResponse.data) ? refreshResponse.data :
            (refreshResponse.data?.ubicaciones && Array.isArray(refreshResponse.data.ubicaciones)) ? refreshResponse.data.ubicaciones : [];
          setUbicaciones(ubicacionesData);
        }

        setUbicacionSeleccionada(null);
        toast.success("Zona eliminada exitosamente.", {
          position: "top-right",
          autoClose: 3000,
        });
        setState({
          ...state,
          apiMessage: "",
        });
      }
    } catch (error) {
      toast.error("Error al eliminar la zona.", {
        position: "top-right",
        autoClose: 4000,
      });
      setState({
        ...state,
        error: "",
      });
    }
  };

  const getTipoZonaTexto = (zona) => {
    switch (zona.tipo_ubicacion) {
      case 1:
        return `👥 General`;
      case 2:
        return `🍽️ Con mesas`;
      case 3:
        return `🪑 Sillas individuales`;
      default:
        return `❓ Desconocido`;
    }
  };

  const ZonaRect = ({ zona, isNueva = false, isSelected = false, onSelect, onTransform }) => {
    const [isHovered, setIsHovered] = useState(false);

    const shapeRef = useRef();
    const trRef = useRef();

    React.useEffect(() => {
      if (isSelected && trRef.current && shapeRef.current) {
        trRef.current.nodes([shapeRef.current]);
        trRef.current.getLayer().batchDraw();
      }
    }, [isSelected]);

    const getTextoCapacidad = (zona) => {
      switch (zona.tipo_ubicacion) {
        case 1:
          return `Capacidad: ${zona.limite_maximo || 0} personas`;
        case 2:
          return `Capacidad: ${zona.limite_maximo || 0} personas (mesas)`;
        case 3:
          return `Capacidad: ${zona.limite_maximo || 0} personas (asientos)`;
        default:
          return `${zona.limite_maximo || 0} personas`;
      }
    };

    return (
      <>
        <Rect
          ref={shapeRef}
          x={zona.coordX}
          y={zona.coordY}
          width={zona.ancho}
          height={zona.alto}
          fill={isNueva ? "#81C784" : isSelected ? "#2196F3" : "#E3F2FD"}
          stroke={isHovered ? "#1976D2" : "#BDBDBD"}
          strokeWidth={isHovered ? 3 : 1}
          draggable={!isNueva}
          onDragEnd={(e) => !isNueva && handleZonaDrag(e, zona)}
          onClick={() => !isNueva && onSelect && onSelect()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onDblClick={() => !isNueva && abrirEditorLayout(zona)}
          onTransformEnd={(e) => onTransform && onTransform(e, zona)}
        />
        <Text
          x={zona.coordX + 10}
          y={zona.coordY + 10}
          text={zona.ubicacion || `Zona ${zona.id}`}
          fontSize={18}
          fontStyle={isSelected ? "bold" : "regular"}
          fontFamily="Bricolage Grotesque, sans-serif"
          fill={isSelected ? "#0B0060" : "black"}
          listening={false}
          width={zona.ancho - 20}
          wrap="word"
        />
        <Text
          x={zona.coordX + 10}
          y={zona.coordY + 30}
          text={getTextoCapacidad(zona)}
          fontSize={14}
          fontFamily="Inter, sans-serif"
          fill={isSelected ? "#ffff" : "black"}
          listening={false}
          width={zona.ancho - 20}
          wrap="word"
        />
        {isSelected && (
          <Transformer
            ref={trRef}
            rotateEnabled={false}
            enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          />
        )}
      </>
    );
  };

  const [showMapaDisponibilidad, setShowMapaDisponibilidad] = useState(false);

  const openMapaDisponibilidad = () => {
    if (!ubicacionSeleccionada) return;
    setShowMapaDisponibilidad(true);
  };

  return (
    <>
      <Box sx={{
        borderTop: '3px solid #e0e0e0',
        bgcolor: '#f8f9fa',
        borderRadius: '8px 8px 0 0',
        p: 3,
        mt: 2,
        minHeight: "100vh",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        overflowX: "auto"
      }}>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            width: "100%",
            maxWidth: "1200px",
            minWidth: "0",
            height: "calc(100vh - 120px)"
          }}>
            {/* CANVAS */}
            <Box ref={canvasContainerRef} sx={{
              flex: 1,
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "10px",
              border: "1px solid #E4E4E7",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: "0",
              height: "calc(100vh - 120px)"
            }}>
              {/* header*/}
              <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Mapa del Evento
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant={modoCreacion ? "contained" : "outlined"}
                    startIcon={<Add />}
                    onClick={() => setModoCreacion(!modoCreacion)}
                    sx={{
                      borderRadius: '6px',
                      fontWeight: 500,
                      ...(modoCreacion ? {
                        bgcolor: '#10b981',
                        color: 'white',
                        '&:hover': { bgcolor: '#059669' }
                      } : {
                        borderColor: '#6366f1',
                        color: '#6366f1',
                        '&:hover': {
                          borderColor: '#4f46e5',
                          bgcolor: 'rgba(99, 102, 241, 0.04)'
                        }
                      })
                    }}
                  >
                    {modoCreacion ? "Crear Zona (Click en el mapa)" : "Crear Zona"}
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    disabled={ubicaciones.length === 0}
                    onClick={guardarDiseno}
                    sx={{
                      bgcolor: '#6366f1',
                      borderRadius: '6px',
                      fontWeight: 500,
                      '&:hover': { bgcolor: '#4f46e5' },
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    Guardar Diseño
                  </Button>
                </Box>
                {/* {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}
                {state.apiMessage && <Alert severity="success" sx={{ mb: 2 }}>{state.apiMessage}</Alert>} */}
              </Box>

              {/* CANVAS */}
              <Paper elevation={0} ref={paperRef}
                sx={{
                  borderRadius: '8px',
                  width: '100%',
                  height: '100%',
                  boxSizing: "border-box",
                  overflow: "hidden",
                  flex: 1,
                  minHeight: 300
                }}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  <Stage
                    width={canvasSize.width}
                    height={canvasSize.height}
                    ref={stageRef}
                    onClick={handleCanvasClick}
                    style={{
                      border: "1px solid #e5e7eb",
                      backgroundColor: "white",
                      borderRadius: '10px',
                      width: "100%",
                      height: "100%",
                      display: "block"
                    }}
                  >
                    <Layer>
                      {Array.isArray(ubicaciones) && ubicaciones.map((zona) => (
                        <ZonaRect
                          key={zona.id}
                          zona={zona}
                          isSelected={ubicacionSeleccionada?.id === zona.id}
                          onSelect={() => handleZonaClick(zona)}
                          onTransform={handleZonaTransform}
                        />
                      ))}
                    </Layer>
                  </Stage>
                </Box>
              </Paper>
            </Box>

            {/* PROPIEDADES */}
            <Box sx={{
              width: 260,
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "10px",
              border: "1px solid #E4E4E7",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: "0"
            }}>
              {/* header */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#0b0060',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Propiedades de la zona
                </Typography>
              </Box>

              <Paper elevation={0} sx={{
                width: "100%",
                overflow: 'hidden'
              }}>
                <Box /* sx={{ p: 2 }} */>
                  {ubicacionSeleccionada ? (
                    <Box>
                      <Box sx={{
                        bgcolor: '#f0f9ff',
                        color: '#5656ff',
                        fontSize: '14px',
                        borderRadius: '8px',
                        mb: 2,
                        p: 2,
                        border: '1px solid #e0f2fe',
                        display: "flex",
                        flexDirection: "column",
                        flexWrap: 'wrap'
                      }}>
                        <p style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "4px", display: "flex", alignItems: "center" }}>{getTipoZonaTexto(ubicacionSeleccionada)}</p>
                        <p style={{ fontSize: "14px", color: "black" }}><span style={{ fontWeight: "bold" }}>Capacidad máxima:</span> {ubicacionSeleccionada.limite_maximo} personas</p>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          fullWidth
                          onClick={() => setShowEditModal(true)}
                          sx={{
                            borderColor: '#E4E4E7',
                            backgroundColor: '#E4E4E7',
                            color: 'black',
                            borderRadius: '10px',
                            '&:hover': { bgcolor: '#BABABB' }
                          }}
                        >
                          Editar zona
                        </Button>

                        <Box>
                          <Button
                            variant="contained"
                            startIcon={<Edit />}
                            fullWidth
                            disabled={ubicacionSeleccionada.tipo.id === 1}
                            onClick={() => abrirEditorLayout(ubicacionSeleccionada)}
                            sx={{
                              mb: ubicacionSeleccionada.tipo.id === 1 ? 0.5 : 2,
                              bgcolor: '#5656ff',
                              borderRadius: '10px',
                              '&:hover': { bgcolor: '#0b0060' }
                            }}
                          >
                            Diseñar zona
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Visibility />}
                            fullWidth
                            disabled={ubicacionSeleccionada.tipo.id === 1}
                            onClick={openMapaDisponibilidad}
                            sx={{
                              mb: ubicacionSeleccionada.tipo.id === 1 ? 0.5 : 1,
                              bgcolor: '#f59e0b',
                              borderRadius: '10px',
                              '&:hover': { bgcolor: '#d97706' },
                              '&:disabled': {
                                bgcolor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                          >
                            Disponibilidad
                          </Button>
                          {ubicacionSeleccionada.tipo.id === 1 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Las zonas tipo General no requieren diseño interno.
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          fullWidth
                          onClick={eliminarZonaSeleccionada}
                          sx={{
                            borderRadius: '10px',
                            borderColor: '#f87171',
                            color: '#dc2626',
                            '&:hover': {
                              borderColor: '#ef4444',
                              bgcolor: 'rgba(239, 68, 68, 0.04)'
                            }
                          }}
                        >
                          Eliminar Zona
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{
                      textAlign: 'center',
                      color: '#9ca3af',
                      py: 4
                    }}>
                      <Typography variant="body2">
                        Selecciona una zona para ver sus propiedades
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Box>

      <EditorLayout
        open={showLayoutEditor}
        onClose={() => {
          setShowLayoutEditor(false);
          setModoCreacion(false);
        }}
        zona={ubicacionSeleccionada}
        onSave={(elementos) => {
          setShowLayoutEditor(false);
          setModoCreacion(false);
        }}
      />

      <CreateZonaModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setClickPosition(null);
          setModoCreacion(false);
        }}
        onConfirm={crearZonaDesdeModal}
        position={clickPosition}
      />

      <EditZonaModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        zona={ubicacionSeleccionada}
        onSave={actualizarZona}
      />

      <DisponibilidadModal
        open={showMapaDisponibilidad}
        onClose={() => setShowMapaDisponibilidad(false)}
        zona={ubicacionSeleccionada}
        eventoId={eventoId}
      />
    </>
  );
};

export default withAuthenticationRequired(Ubicaciones, {
  onRedirecting: () => <Loading />,
});